// Vykreslení spočítaného widgetu komponentami UI kitu.
import { BarChart, Calendar, CHART_PALETTE, DonutChart, LineChart, PieChart, ProgressBar, Stat, Table } from '@smworks-cz/ui-kit';
import { formatValue, type WidgetModel } from '@sam/shared';

/**
 * Stejná kategorie = stejná barva napříč widgety (sloupce podle klienta i koláč podle klienta).
 * Barva se odvodí z názvu, ne z pořadí v grafu.
 */
const colorCache = new Map<string, string>();
function colorFor(label: string): string {
  let c = colorCache.get(label);
  if (!c) {
    let h = 0;
    for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0;
    // Volné barvy přednostně, ať se v jednom grafu neopakují.
    const used = new Set(colorCache.values());
    const free = CHART_PALETTE.filter((x) => !used.has(x));
    const pool = free.length ? free : CHART_PALETTE;
    c = pool[h % pool.length];
    colorCache.set(label, c);
  }
  return c;
}

/**
 * `compact` je malá karta, `maxRows` zkrátí tabulku, `tile` = karta na přehledu
 * (kalendář vždy velký a bez hlavičky, měsíc podle první události).
 */
export function WidgetView({ model, compact = false, maxRows, tile = false }: { model: WidgetModel; compact?: boolean; maxRows?: number; tile?: boolean }) {
  switch (model.type) {
    case 'stats':
      return (
        <div className="w-stats">
          {model.items.slice(0, compact ? 2 : 6).map((it, i) => (
            <Stat key={i} label={it.label} value={it.value} helper={it.hint} />
          ))}
        </div>
      );
    case 'bar': {
      const fmt = (v: number) => formatValue(v, model.format);
      const height = model.horizontal ? Math.min(560, Math.max(140, model.categories.length * 34 + 40)) : compact ? 150 : 260;
      return (
        <BarChart
          series={model.series.length > 1 ? model.series.map((se) => ({ ...se, color: colorFor(se.name) })) : model.series}
          categories={model.categories}
          orientation={model.horizontal ? 'horizontal' : 'vertical'}
          // Vodorovné sloupce: místo vlevo podle nejdelšího popisku, ať se neořízne.
          margin={model.horizontal ? { left: Math.min(180, 16 + Math.max(...model.categories.map((c) => c.length)) * 7) } : undefined}
          stacked={model.stacked}
          height={height}
          valueFormat={fmt}
          legend={model.series.length > 1 && !compact ? 'bottom' : 'none'}
          ariaLabel="Sloupcový graf"
        />
      );
    }
    case 'line':
    case 'area':
      return (
        <LineChart
          series={model.series}
          categories={model.categories}
          area={model.type === 'area'}
          height={compact ? 150 : 260}
          valueFormat={(v) => formatValue(v, model.format)}
          legend={model.series.length > 1 && !compact ? 'bottom' : 'none'}
          ariaLabel="Spojnicový graf"
        />
      );
    case 'donut':
    case 'pie': {
      const Chart = model.type === 'donut' ? DonutChart : PieChart;
      return (
        <div className="w-pie">
          <Chart
            data={model.data.map((d) => ({ ...d, color: colorFor(d.label) }))}
            size={compact ? 140 : 200}
            legend={compact ? 'none' : 'right'}
            valueFormat={(v) => formatValue(v, model.format)}
            centerLabel={model.type === 'donut' ? <span className="w-pie-total">{formatValue(model.total, model.format)}</span> : undefined}
            ariaLabel="Koláčový graf"
          />
        </div>
      );
    }
    case 'progress':
      return (
        <div className="w-progress">
          {model.items.slice(0, compact ? 3 : 50).map((it, i) => (
            <div key={i} className="w-progress-row">
              <span className="w-progress-label">{it.label}</span>
              <ProgressBar
                value={Math.min(it.pct, 100)}
                max={100}
                thresholds={[
                  { value: 0, variant: 'success' },
                  { value: 80, variant: 'warning' },
                  { value: 100, variant: 'danger' },
                ]}
                size="sm"
              />
              <span className="w-progress-val">{it.text}</span>
            </div>
          ))}
        </div>
      );
    case 'table': {
      const rows = model.rows.slice(0, compact ? 4 : (maxRows ?? model.rows.length));
      return (
        <div className="w-table">
          <Table
            columns={model.columns.map((c, i) => ({
              key: `c${i}`,
              header: c.label,
              render: (v: string) => <span style={c.numeric ? { display: 'block', textAlign: 'right', whiteSpace: 'nowrap' } : undefined}>{v}</span>,
            }))}
            data={rows.map((r) => Object.fromEntries(r.map((v, i) => [`c${i}`, v])))}
            emptyText="Žádné řádky"
            hoverable
          />
          {model.total > rows.length && (
            <p className="muted small">
              Zobrazeno {rows.length} z {model.total} řádků.
            </p>
          )}
        </div>
      );
    }
    case 'calendar': {
      const events = model.events.map((e, i) => ({
        id: String(i),
        date: new Date(e.start),
        endDate: new Date(e.end),
        title: e.time ? `${e.time} ${e.title}` : e.title,
        color: colorFor(e.category ?? 'kalendář'),
        tooltip: [e.time, e.title, e.detail].filter(Boolean).join(' · '),
      }));
      const initialDate = model.initial ? new Date(`${model.initial}T12:00:00`) : undefined;
      // Kompaktní kalendář (tečky) hlavičku skrýt neumí; velký ano.
      if (tile) {
        return (
          <div className="w-cal tile">
            <Calendar fullSize events={events} initialDate={initialDate} showHeader={false} maxEventsPerDay={compact ? 1 : 3} />
          </div>
        );
      }
      return compact ? (
        <div className="w-cal compact">
          <Calendar size="sm" events={events} initialDate={initialDate} value={null} />
        </div>
      ) : (
        <div className="w-cal">
          <Calendar fullSize events={events} initialDate={initialDate} showHeader={model.header} maxEventsPerDay={4} />
        </div>
      );
    }
    case 'kanban':
      return (
        <div className="w-kanban">
          {model.columns.map((c) => (
            <div key={c.name} className="w-kan-col">
              <h4>
                {c.name} <span className="muted">{c.cards.length}</span>
              </h4>
              {c.cards.slice(0, compact ? 2 : 50).map((k, i) => (
                <div key={i} className="w-kan-card">
                  {k.title}
                  {k.detail && <small>{k.detail}</small>}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
  }
}
